import { useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '@/stores/store';
import {
  addMessage,
  addOrUpdateMessage,
  setStreamingStatus,
  setError,
  addIntermediateStep,
  setCurrentThread,
  addCustomEventValue,
  upsertCustomEventValue,
  setCurrentTeam,
  setTeamLeader,
  setThreads,
  setTyping,
  setPendingInterrupt,
  type ComponentMetadata,
  type CustomEventData,
} from '@/stores/slices/chat-slice';
import {
  selectMessages,
  selectIsTyping,
  selectStreamingStatus,
  selectCurrentThreadId,
  selectCustomEventValues,
  selectStreamAbortToken,
} from '@/stores/selectors/chat-selectors';
import { selectUser } from '@/stores/selectors/user-selectors';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { chatAssistantService, type SendMessageInput } from '@/services/chat-assistant-service';
import { getLangGraphClient, langGraphClient as defaultLangGraphClient } from '@/lib/langgraph-client-v2';
import { getTeamLeader } from '@/services/onboarding-steps.service';
import type { StreamCallbacks, StreamController } from '@/types/langgraph';
import { getContentString } from '@/components/thread/utils';
import { DO_NOT_RENDER_ID_PREFIX } from '@/lib/ensure-tool-responses';

/**
 * Custom hook for chat operations
 * Provides methods to send messages and access chat state from Redux
 */
export function useChat() {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages);
  const isTyping = useAppSelector(selectIsTyping);
  const streamingStatus = useAppSelector(selectStreamingStatus);
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  const customEventValues = useAppSelector(selectCustomEventValues);
  const user = useAppSelector(selectUser);
  const selectedAgent = useAppSelector(selectSelectedAgent);

  const streamAbortToken = useAppSelector(selectStreamAbortToken);

  // Active stream controller — used by stopStream to abort in-flight requests
  const activeControllerRef = useRef<StreamController | null>(null);

  // Keep a ref to the latest streamAbortToken so callbacks can detect invalidation
  const streamAbortTokenRef = useRef(streamAbortToken);
  useEffect(() => {
    streamAbortTokenRef.current = streamAbortToken;
    // Abort any in-flight stream when the token changes (clearMessages / newChat)
    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
      activeControllerRef.current = null;
      dispatch(setStreamingStatus('idle'));
      dispatch(setTyping(false));
    }
  }, [streamAbortToken, dispatch]);

  // Keep a ref to the latest customEventValues for duplicate checking
  const customEventValuesRef = useRef(customEventValues);
  useEffect(() => {
    customEventValuesRef.current = customEventValues;
  }, [customEventValues]);

  const stopStream = useCallback(() => {
    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
    dispatch(setStreamingStatus('idle'));
    dispatch(setTyping(false));
    dispatch(setPendingInterrupt(null));
  }, [dispatch]);

  /**
   * Send a message to the assistant with streaming response
   * Supports both string input (backward compatible) and message array for tool messages
   */
  const sendMessage = useCallback(
    async (input: SendMessageInput, userId: string): Promise<StreamController | null> => {
      dispatch(setStreamingStatus('streaming'));

      const client = selectedAgent
        ? getLangGraphClient(selectedAgent.id)
        : defaultLangGraphClient;

      // Capture the abort token at stream start — any clearMessages() call will increment
      // this token and invalidate all callbacks from this stream session.
      const sessionAbortToken = streamAbortTokenRef.current;

      // Create thread if needed
      let threadId = currentThreadId;
      if (!threadId) {
        // Pass subdomain in metadata if available from user (captured during OAuth)
        const metadata = user?.subdomain ? { subdomain: user.subdomain } : undefined;
        const [error, thread] = await chatAssistantService.createThread(userId, metadata, client);
        if (error || !thread) {
          dispatch(setError(error?.message || 'Failed to create thread'));
          return null;
        }
        threadId = thread.langGraphThreadId;
        dispatch(setCurrentThread(threadId));
      }

      // Add messages to Redux for UI display
      if (typeof input === 'string') {
        // Backward compatibility: string input
        dispatch(
          addMessage({
            id: uuidv4(),
            role: 'user',
            content: input,
            timestamp: Date.now(),
          })
        );
      } else {
        // New format: message array - only show human messages, skip tool results
        // Also skip internal messages prefixed with DO_NOT_RENDER_ID_PREFIX
        // Supports multimodal content (text + images)
        input
          .filter((msg) => msg.type === 'human' && !msg.id?.startsWith(DO_NOT_RENDER_ID_PREFIX))
          .forEach((msg) => {
            let displayContent: string;
            let imageDataUri: string | undefined;

            if (typeof msg.content === 'string') {
              displayContent = msg.content;
            } else {
              // Extract text and image from content blocks
              const textBlocks = msg.content.filter((b) => b.type === 'text') as Array<{ type: 'text'; text: string }>;
              const imageBlocks = msg.content.filter((b) => b.type === 'image') as Array<{ type: 'image'; source: { type: 'base64'; media_type: string; data: string } }>;
              displayContent = textBlocks.map((b) => b.text).join('\n').trim() || '📷 Image attached';
              if (imageBlocks.length > 0) {
                const imgBlock = imageBlocks[0];
                imageDataUri = `data:${imgBlock.source.media_type};base64,${imgBlock.source.data}`;
              }
            }

            dispatch(
              addMessage({
                id: msg.id || uuidv4(),
                role: 'user',
                content: displayContent,
                timestamp: Date.now(),
                imageDataUri,
              })
            );
          });
      }

      // Track the assistant message ID when it arrives from backend
      let currentAssistantMessageId: string | null = null;

      // Stream response
      const callbacks: StreamCallbacks = {
        onToken: (messageId, token) => {
          if (streamAbortTokenRef.current !== sessionAbortToken) return;
          // Store messageId on first occurrence
          if (!currentAssistantMessageId) {
            currentAssistantMessageId = messageId;
          }

          // Create on first token, update on subsequent tokens
          dispatch(
            addOrUpdateMessage({
              id: messageId,  // Backend ID
              content: token,
              role: 'assistant',
              isStreaming: true,
            })
          );
        },
        onComplete: (message) => {
          if (streamAbortTokenRef.current !== sessionAbortToken) return;
          const extractedContent = getContentString(message.content);

          // Use extracted content to handle both string and object formats
          dispatch(
            addOrUpdateMessage({
              id: message.id,  // Backend ID
              content: extractedContent,
              role: 'assistant',
              isStreaming: false,
            })
          );
        },
        onCustomEvent: (event) => {
          if (streamAbortTokenRef.current !== sessionAbortToken) return;
          // FILTER OUT application_selector_ui - it's been removed from the project
          if (event?.type === 'ui' && event?.name === 'application_selector_ui') {
            return;
          }

          // Handle UI/MCP_APP events - use current assistant message ID as fallback if metadata.message_id is missing
          if (event?.type === 'ui' || event?.type === 'mcp_app') {
            // If no message_id in metadata, use the current streaming message ID
            if (!event?.metadata?.message_id && currentAssistantMessageId) {
              event.metadata = event.metadata || {};
              event.metadata.message_id = currentAssistantMessageId;
            }
          }

          // Store custom event data in Redux
          if (event && event.type) {
            const isUiEvent = event.type === 'ui' || event.type === 'mcp_app';

            // For non-UI events keep the existing skip-duplicate behaviour —
            // these events are informational and should only be recorded once.
            if (!isUiEvent) {
              const eventId = event.id;
              if (eventId) {
                const isDuplicate = customEventValuesRef.current.some(
                  (existingEvent) => existingEvent.id === eventId
                );
                if (isDuplicate) {
                  return;
                }
              }
            }

            const eventData: CustomEventData = {
              type: event.type,
              timestamp: Date.now(),
              ...(event.type === 'ui' && {
                id: event.id,
                name: event.name,
                props: event.props,
                metadata: event.metadata,
              }),
              ...(event.type === 'mcp_app' && {
                id: event.id,
                name: event.name,
                props: event.props,
                metadata: event.metadata,
              }),
              ...(event.type === 'phase_update' && {
                id: event.id,
                name: event.name,
              }),
              ...(event.type === 'current_team_update' && {
                id: event.id,
                name: event.name,
              }),
              ...(event.type === 'team_leader_update' && {
                id: event.id,
                name: event.name,
              }),
            };

            // UI events (ui / mcp_app) are upserted by id so that re-emits with
            // the same id replace the existing card in place rather than appending.
            // Non-UI events use the push-only reducer (duplicates already skipped above).
            if (isUiEvent) {
              dispatch(upsertCustomEventValue(eventData));
            } else {
              dispatch(addCustomEventValue(eventData));
            }

            // Handle current_team_update event
            if (event.type === 'current_team_update' && threadId) {
              chatAssistantService.getCurrentTeam(threadId).then(([error, team]) => {
                if (team) {
                  dispatch(setCurrentTeam(team));
                } else {
                  dispatch(setCurrentTeam(null));
                }
              });
            }

            // Handle team_leader_update event
            if (event.type === 'team_leader_update' && threadId) {
              getTeamLeader(threadId).then(([error, leader]) => {
                if (leader) {
                  dispatch(setTeamLeader(leader));
                } else {
                  dispatch(setTeamLeader(null));
                }
              });
            }
          }
        },
        onError: (error, hasMessageId) => {
          dispatch(setError(error.message));

          // Use backend ID if available, otherwise create error ID
          const errorId = (hasMessageId && currentAssistantMessageId)
            ? currentAssistantMessageId
            : `__error_${Date.now()}`;

          dispatch(
            addOrUpdateMessage({
              id: errorId,  // Backend ID or special error ID
              content: 'Error: Failed to get response',
              role: 'assistant',
              isStreaming: false,
              isError: true,
              errorMessage: error.message,
            })
          );
        },
        onIntermediateStep: (step) => {
          dispatch(addIntermediateStep(step));
        },
        onEnd: () => {
          dispatch(setStreamingStatus('completed'));
          // Refresh thread titles from the backend so the sidebar reflects the
          // updated title that sync_thread_title() wrote during this stream.
          // Pinned to the active agent so the search endpoint filters by
          // ``agent_id`` and we don't mix threads across agents.
          chatAssistantService
            .fetchRecentThreads(
              5,
              selectedAgent ? getLangGraphClient(selectedAgent.id) : undefined,
            )
            .then(([, fetched]) => {
              if (fetched) dispatch(setThreads(fetched));
            });
          // Sync current team from thread metadata after every stream. The
          // save_current_team tool commits to DB during the stream, so reading
          // it here is guaranteed to be up-to-date.
          if (threadId) {
            chatAssistantService.getCurrentTeam(threadId).then(([, team]) => {
              dispatch(setCurrentTeam(team ?? null));
            });
          }
        },
      };

      const [error, controller] = await chatAssistantService.sendMessageStream(
        threadId,
        input,
        callbacks,
        undefined,
        client
      );

      if (error) {
        dispatch(setError(error.message));
        return null;
      }

      activeControllerRef.current = controller;
      return controller;
    },
    [currentThreadId, dispatch, selectedAgent]
  );

  return {
    messages,
    isTyping,
    streamingStatus,
    stopStream,
    currentThreadId,
    customEventValues,
    sendMessage,
  };
}
